import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Typography, Stack, ToggleButton, ToggleButtonGroup, FormControl, FormLabel, Select, MenuItem, Tooltip } from '@mui/material';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import {
    useRejectItemImageReviewMutation,
    useApproveItemImageReviewMutation,
    useGetItemImagesInfiniteQuery,
    useGetItemImagesInfiniteOrganizationsFilterDataQuery,
    useGetItemImagesInfiniteHospitalsFilterDataQuery,
    useGetItemImagesInfiniteVendorsFilterDataQuery,
    useGetItemImagesInfiniteCategoriesFilterDataQuery,
    useGetItemImagesInfiniteCampaignsFilterDataQuery,
} from '../../../api/itemSlice';
import { useAddItemImageMutation, useAddPackItemImageMutation } from '../../../api/itemDetailSlice';
import ItemImageReviewDetailPage from './ItemImageReviewDetailPage';
import ItemImageReviewMultiSelect from './ItemImageReviewMultiSelect';
import UploadImage from '../../CustomComponents/UploadImage';

function ItemImageReview() {
    const tableContainerRef = useRef(null);
    const rowVirtualizerInstanceRef = useRef(null);
    const isFetchingNextPageRef = useRef(false);

    const fetchSize = 25;

    const [rejectImage] = useRejectItemImageReviewMutation();
    const [approveImage] = useApproveItemImageReviewMutation();
    const [addItemImage, { isLoading: isUploadingImage }] = useAddItemImageMutation();
    const [addPackItemImage, { isLoading: isUploadingPackItemImage }] = useAddPackItemImageMutation();
    const [page, setPage] = useState(1);
    
    // Track image changes for each item to trigger re-renders
    const [imageChanges, setImageChanges] = useState({});
    
    // Track image upload state
    const [isUploadingImageForItem, setIsUploadingImageForItem] = useState(null);
    
    // Track which rows have been auto-collapsed to prevent repeated collapsing
    const [autoCollapsedRows, setAutoCollapsedRows] = useState(new Set());
    
    // Track filtered out values that should be restored when they become valid again
    const [notValidFilters, setNotValidFilters] = useState({
        vendor: new Set(),
        category: new Set()
    });

    // Parse URL params once during initialization
    const urlParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        let sorting = [];
        let columnFilters = [];

        if (params.get('sort')) {
            try {
                sorting = JSON.parse(params.get('sort'));
            } catch {
                sorting = [];
            }
        }
        if (params.get('filters')) {
            try {
                columnFilters = JSON.parse(params.get('filters'));
            } catch {
                columnFilters = [];
            }
        }

        // Add default validation filter if not present
        const hasValidationFilter = columnFilters.some(filter => filter.id === 'validationFilter');
        if (!hasValidationFilter) {
            columnFilters.push({ id: 'validationFilter', value: 'unvalidated' });
        }

        // Fix validation filter if "no-images" is selected without an organization
        const hasOrganization = columnFilters.some(filter => filter.id === 'organization' && filter.value);
        const validationFilterIndex = columnFilters.findIndex(filter => filter.id === 'validationFilter');
        if (!hasOrganization && validationFilterIndex !== -1 && columnFilters[validationFilterIndex].value === 'no-images') {
            columnFilters[validationFilterIndex].value = 'unvalidated';
        }

        return { sorting, columnFilters };
    }, []);
    
    const [columnFilters, setColumnFilters] = useState(() => {
        return urlParams.columnFilters;
    });

    const [sorting, setSorting] = useState(() => {
        return urlParams.sorting;
    });

    // Helper functions to get filter values from columnFilters
    const getFilterValue = useCallback(
        (filterId) => {
            const filter = columnFilters.find(f => f.id === filterId);
            return filter?.value || '';
        },
        [columnFilters]
    );

    const validationFilter = useMemo(() => getFilterValue('validationFilter'), [getFilterValue]);
    const selectedOrganization = useMemo(() => getFilterValue('organization'), [getFilterValue]);
    const selectedHospital = useMemo(() => getFilterValue('hospital'), [getFilterValue]);
    const selectedCampaign = useMemo(() => getFilterValue('campaign'), [getFilterValue]);
    const includePackItems = useMemo(() => getFilterValue('includePackItems'), [getFilterValue]);
    
    const [queryParams, setQueryParams] = useState(() => {
        return {
            page: 1,
            perPage: fetchSize,
            sorting: urlParams.sorting,
            columnFilters: urlParams.columnFilters,
            isPackItems: includePackItems === 'pack-items-only',
        };
    });

    // --- URL persistence helpers ---
    const serializeQueryParams = useCallback((params) => {
        const urlParams = new URLSearchParams();
        
        if (params.sorting && params.sorting.length)
            urlParams.set('sort', JSON.stringify(params.sorting));
        if (params.columnFilters && params.columnFilters.length)
            urlParams.set('filters', JSON.stringify(params.columnFilters));
        return urlParams.toString();
    }, []);

    // --- Update URL when queryParams change (NOT Page) ---
    useEffect(() => {
        const query = serializeQueryParams({
            sorting: queryParams.sorting,
            columnFilters: queryParams.columnFilters
        });
        const newUrl = query ? `?${query}` : window.location.pathname;
        window.history.replaceState(null, '', newUrl);
    }, [queryParams.sorting, queryParams.columnFilters, serializeQueryParams]);

    // Update query params only when page changes (triggers API call)
    useEffect(() => {
        if (page === 1) return; // Skip initial page 1 set
        setQueryParams(prev => ({
            ...prev,
            page
        }));
    }, [page]);

    // Update query params when sorting/filtering changes AND reset page (triggers API call)
    useEffect(() => {
        setQueryParams({
            isPackItems: includePackItems === 'pack-items-only',
            page: 1,
            perPage: fetchSize,
            columnFilters,
            sorting: includePackItems === 'pack-items-only' ? undefined : sorting,
        });
        setPage(1);
        setAutoCollapsedRows(new Set());
    }, [includePackItems, sorting, columnFilters]);

    const { 
        data: infiniteData, 
        isLoading: isTableDataLoading, 
        isError: isTableError, 
        isFetching: isFetching,
    } = useGetItemImagesInfiniteQuery(queryParams);

    const { 
        data: organizationsFilterValues, 
        isFetching: isFetchingOrganizationsFilterValues
    } = useGetItemImagesInfiniteOrganizationsFilterDataQuery({
        columnFilters
    });
    
    const { 
        data: hospitalsFilterValues, 
        isFetching: isFetchingHospitalsFilterValues
    } = useGetItemImagesInfiniteHospitalsFilterDataQuery({
        columnFilters
    });
    
    const { 
        data: vendorsFilterValues, 
        isFetching: isFetchingVendorsFilterValues
    } = useGetItemImagesInfiniteVendorsFilterDataQuery({
        columnFilters
    }, {
        skip: includePackItems === 'pack-items-only'
    });
    
    const { 
        data: categoriesFilterValues, 
        isFetching: isFetchingCategoriesFilterValues
    } = useGetItemImagesInfiniteCategoriesFilterDataQuery({
        columnFilters
    }, {
        skip: includePackItems === 'pack-items-only'
    });
    
    const { 
        data: campaignsFilterValues, 
        isFetching: isFetchingCampaignsFilterValues
    } = useGetItemImagesInfiniteCampaignsFilterDataQuery({
        columnFilters
    }, {
        skip: includePackItems === 'pack-items-only'
    });

    const processFilterOptions = useCallback((options, dataKey) => {
        if (!options || typeof options !== 'object') {
            return [];
        }
        
        // Handle both array and object responses
        let optionsArray = Array.isArray(options) ? options : options[dataKey];
        
        if (!Array.isArray(optionsArray) || optionsArray.length === 0) {
            return [];
        }
        
        return optionsArray.map(item => ({
            label: String(item?.label || item?.name || ''),
            value: String(item?.value || item?.id || item?.name || '')
        })).filter(item => item.label && item.value);
    }, []);

    const vendorList = useMemo(() => {
        return processFilterOptions(vendorsFilterValues, 'vendors');
    }, [vendorsFilterValues, processFilterOptions]);
    
    const categoryList = useMemo(() => {
        return processFilterOptions(categoriesFilterValues, 'categories');
    }, [categoriesFilterValues, processFilterOptions]);
    const organizationList = processFilterOptions(organizationsFilterValues, 'organizations');
    const hospitalList = processFilterOptions(hospitalsFilterValues, 'hospitals');
    const campaignList = processFilterOptions(campaignsFilterValues, 'campaigns');

    const flatData = useMemo(() => {
        if (!infiniteData?.data) return [];

        const itemsMapped = infiniteData.data
            .map(item => ({
                ...item,
                cost: parseFloat(item.cost / 100),
                images: item.images ?? [],
                type: 'item',
                vendor: {
                    ...item.vendor,
                    name: item.vendor_name || item.vendor?.name
                },
                part_number: item.part_number || item.catalog_number,
            }));

        return itemsMapped;
    }, [infiniteData, includePackItems, fetchSize]);

    const totalDBRowCount = infiniteData?.meta?.total ?? 0;
    const totalFetched = infiniteData?.data?.length || 0;

    // Combine all filter loading states
    const isAnyFilterLoading = isFetchingOrganizationsFilterValues || isFetchingHospitalsFilterValues || isFetchingVendorsFilterValues || isFetchingCategoriesFilterValues || isFetchingCampaignsFilterValues;

    // Function to load more data
    const fetchNextPage = useCallback(() => {
        if (totalFetched < totalDBRowCount && !isFetching && !isAnyFilterLoading && !isFetchingNextPageRef.current) {
            isFetchingNextPageRef.current = true;
            setPage(prevPage => prevPage + 1);
        }
    }, [totalFetched, totalDBRowCount, isFetching, isAnyFilterLoading]);

    // Reset the fetching ref when a fetch completes
    useEffect(() => {
        if (!isFetching) {
            isFetchingNextPageRef.current = false;
        }
    }, [isFetching]);

    // Auto-fetch more data when pack items don't meet page size requirement
    useEffect(() => {
        if (includePackItems === 'pack-items-only' && 
            !isFetching && 
            !isAnyFilterLoading && 
            !isFetchingNextPageRef.current &&
            infiniteData?.data &&
            totalFetched < totalDBRowCount) {
            
            const allPackItems = infiniteData.data
                .filter(item => item.pack_items && item.pack_items.length > 0)
                .flatMap(item => item.pack_items);
            
            const dedupedPackItemsCount = new Set(allPackItems.map(pi => pi.id)).size;
            
            // If we don't have enough pack items and there's more data to fetch
            if (dedupedPackItemsCount < fetchSize) {
                fetchNextPage();
            }
        }
    }, [includePackItems, infiniteData, isFetching, isAnyFilterLoading, totalFetched, totalDBRowCount, fetchNextPage, fetchSize]);

    //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement) => {
            if (containerRefElement && !isFetchingNextPageRef.current) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (scrollHeight - scrollTop - clientHeight < 400) {
                    fetchNextPage();
                }
            }
        },
        [fetchNextPage],
    );

    // Scroll to top when sorting/filtering changes
    useEffect(() => {
        try {
            rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
        } catch (error) {
            console.error(error);
        }
    }, [sorting, columnFilters]);

    // Helper function to update a specific filter
    const updateFilter = useCallback((id, value) => {
        setColumnFilters(prev => {
            const updated = prev.filter(f => f.id !== id);
            if (value) updated.push({ id, value });
            return updated;
        });
    }, []);

    // Get current filter values for any filter
    const getFilterValues = useCallback((filterId) => {
        const filter = columnFilters.find(f => f.id === filterId);
        return Array.isArray(filter?.value) ? filter.value : (filter?.value ? [filter.value] : []);
    }, [columnFilters]);

    // Shared cleanup logic for filter consistency with restoration capability
    const cleanupFilterValues = useCallback((filterId, availableOptions) => {
        const currentValues = getFilterValues(filterId);
        const currentNotValid = notValidFilters[filterId] || new Set();
        
        const validValues = currentValues.filter(value => availableOptions.includes(value));
        const invalidValues = currentValues.filter(value => !availableOptions.includes(value));
        
        // Check if any previously invalid values are now valid again
        const restoredValues = Array.from(currentNotValid).filter(value => availableOptions.includes(value));
        const allValidValues = [...new Set([...validValues, ...restoredValues])];
        
        // Update not valid filters - remove restored values, add new invalid values
        const updatedNotValid = new Set([...currentNotValid]);
        restoredValues.forEach(value => updatedNotValid.delete(value));
        invalidValues.forEach(value => updatedNotValid.add(value));
        
        // Update state if there are changes
        let hasChanges = false;
        
        // Update not valid filters if changed
        if (updatedNotValid.size !== currentNotValid.size || 
            !Array.from(updatedNotValid).every(v => currentNotValid.has(v))) {
            setNotValidFilters(prev => ({
                ...prev,
                [filterId]: updatedNotValid
            }));
            hasChanges = true;
        }
        
        // Update filter if selected values changed
        if (currentValues.length !== allValidValues.length || 
            !currentValues.every(v => allValidValues.includes(v))) {
            updateFilter(filterId, allValidValues.length > 0 ? allValidValues : undefined);
            hasChanges = true;
        }
        
        return hasChanges;
    }, [getFilterValues, updateFilter, notValidFilters, setNotValidFilters]);

    // Clean up vendor filter values when vendorList changes
    useEffect(() => {
        const vendorOptions = vendorList.map(option => option.value);
        cleanupFilterValues('vendor', vendorOptions);
    }, [vendorList, cleanupFilterValues]);

    // Clean up category filter values when categoryList changes
    useEffect(() => {
        const categoryOptions = categoryList.map(option => option.value);
        cleanupFilterValues('category', categoryOptions);
    }, [categoryList, cleanupFilterValues]);

    const VendorFilter = useMemo(() => {
        const Component = () => (
            <ItemImageReviewMultiSelect
                filterId="vendor"
                options={vendorList}
                selectedValues={getFilterValues('vendor')}
                onUpdateFilter={updateFilter}
                isLoading={isFetchingVendorsFilterValues}
            />
        );
        Component.displayName = 'VendorFilter';
        return Component;
    }, [vendorList, getFilterValues, updateFilter, isFetchingVendorsFilterValues]);

    const CategoryFilter = useMemo(() => {
        const Component = () => (
            <ItemImageReviewMultiSelect
                filterId="category"
                options={categoryList}
                selectedValues={getFilterValues('category')}
                onUpdateFilter={updateFilter}
                isLoading={isFetchingCategoriesFilterValues}
            />
        );
        Component.displayName = 'CategoryFilter';
        return Component;
    }, [
        categoryList, 
        getFilterValues, 
        updateFilter, 
        isFetchingCategoriesFilterValues
    ]);

    const columns = useMemo(() => [
        {
            accessorKey: 'name',
            header: 'Display Name',
            enableColumnFilter: false,
        },
        {
            accessorKey: 'system_name',
            header: 'System Name',
            enableColumnFilter: false,
        },
        {
            accessorKey: 'vendor.name',
            id: 'vendor',
            header: 'Vendor Name',
            filterVariant: 'multi-select',
            filterSelectOptions: vendorList,
            enableColumnFilter: includePackItems !== 'pack-items-only' && !isFetchingVendorsFilterValues,
            Filter: VendorFilter,
        },
        {
            accessorKey: 'part_number',
            header: 'Vendor Part Number',
            enableColumnFilter: false,
        },
        {
            accessorKey: 'category',
            header: 'Category',
            filterVariant: 'multi-select',
            filterSelectOptions: categoryList,
            enableColumnFilter: includePackItems !== 'pack-items-only' && !isFetchingCategoriesFilterValues,
            Filter: CategoryFilter,
        },
        {
            accessorKey: 'hospital.name',
            id: 'hospital',
            header: 'Hospital Name',
            filterVariant: 'multi-select',
            filterSelectOptions: hospitalList,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'cost',
            header: 'Price (Each)',
            Cell: ({ cell }) => {
                return cell.getValue().toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                });
            },
            enableColumnFilter: includePackItems !== 'pack-items-only',
            filterVariant:  'text',
        },
        {
            accessorKey: 'approved_image_count',
            header: 'Validated Images',
            enableColumnFilter: includePackItems !== 'pack-items-only',
            filterVariant: 'text',
        },
        {
            accessorKey: 'card_count',
            header: 'Cards',
            enableColumnFilter: includePackItems !== 'pack-items-only',
            filterVariant: 'text',
        },
        {
            accessorKey: 'recommendation_count', 
            header: 'Recommendations',
            enableColumnFilter: includePackItems !== 'pack-items-only',
            filterVariant: 'text',
        },
    ], [
        vendorList, 
        isFetchingVendorsFilterValues, 
        VendorFilter,
        categoryList, 
        isFetchingCategoriesFilterValues, 
        CategoryFilter,
        hospitalList,
        includePackItems
    ]);

    const table = useMaterialReactTable({
        columns,
        data: flatData,
        enablePagination: false,
        enableRowNumbers: false,
        enableRowVirtualization: true,
        enableGlobalFilter: false,
        initialState: { showColumnFilters: true, columnVisibility: { hospital: false, system_name: false, part_number: false } },
        manualFiltering: true,
        manualSorting: includePackItems !== 'pack-items-only',
        enableExpandAll: true,
        enableExpanding: true,
        enableBottomToolbar: true,
        enableTopToolbar: true,
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                backgroundColor: 
                autoCollapsedRows.has(row.original.id) || row.original.images.filter(img => !img.is_manually_validated).length === 0 ? '#e8f5e8' : 'inherit',
                '&:hover': {
                    backgroundColor: autoCollapsedRows.has(row.original.id) || row.original.images.filter(img => !img.is_manually_validated).length === 0 ? '#d4edda' : 'inherit',
                }
            }
        }),
        renderDetailPanel: ({ row }) => getDetailPage(row, row.index),
        muiTableContainerProps: {
            ref: tableContainerRef, //get access to the table container element
            sx: { 
                maxHeight: 'calc(100vh - 375px)', // Viewport height minus space for header and padding
            },
            onScroll: (event) =>
                fetchMoreOnBottomReached(event.target), //add an event listener to the table container element
        },
        muiToolbarAlertBannerProps: isTableError
            ? {
                color: 'error',
                children: 'Error loading data',
            }
            : undefined,
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        renderBottomToolbarCustomActions: () => (
            <Typography>
                Fetched {totalFetched} of {totalDBRowCount} total rows.
            </Typography>
        ),
        state: {
            columnFilters,
            isTableLoading: isTableDataLoading || isAnyFilterLoading,
            showAlertBanner: isTableError,
            showProgressBars: isFetching,
            sorting,
        },
        rowVirtualizerInstanceRef, //get access to the virtualizer instance
        rowVirtualizerOptions: { 
            overscan: 4,
            estimateSize: () => 50, // Estimate row height for better performance
        },
    });
    
    // Wrapper functions to handle image state updates
    const handleValidateImage = useCallback(async ({ imageId, itemId, isManuallyValidated }) => {
        try {
            await approveImage({ imageId, isManuallyValidated });
            // Update local state to move validated image to end of list
            setImageChanges(prev => {
                const updatedChanges = {
                    ...prev,
                    [itemId]: {
                        ...prev[itemId],
                        validatedImages: [...(prev[itemId]?.validatedImages || []), imageId],
                        rejectedImages: (prev[itemId]?.rejectedImages || []).filter(id => id !== imageId), // Remove from rejected if present
                        timestamp: Date.now() // Force re-render
                    }
                };

                return updatedChanges;
            });
        } catch (error) {
            console.error('Failed to validate image:', error);
        }
    }, [approveImage]);

    const handleRejectImage = useCallback(async ({ image, itemId }) => {
        try {
            await rejectImage({ image });
            // Update local state to remove rejected image
            setImageChanges(prev => {
                const updatedChanges = {
                    ...prev,
                    [itemId]: {
                        ...prev[itemId],
                        rejectedImages: [...(prev[itemId]?.rejectedImages || []), image.id],
                        validatedImages: (prev[itemId]?.validatedImages || []).filter(id => id !== image.id), // Remove from validated if present
                        timestamp: Date.now() // Force re-render
                    }
                };

                return updatedChanges;
            });
        } catch (error) {
            console.error('Failed to reject image:', error);
        }
    }, [rejectImage]);

    // Handle image upload
    const handleUploadImage = useCallback(async (formData) => {
        try {
            // '1' = true
            // The form data contains the image file and any additional metadata
            // We add is_manually_validated to indicate that this image has been manually checked before upload
            formData.append('is_manually_validated', '1');
            
            if (includePackItems === 'pack-items-only') {
                await addPackItemImage({
                    packItemId: isUploadingImageForItem,
                    formData: formData
                }).unwrap();
            } else {
                await addItemImage({
                    itemId: isUploadingImageForItem,
                    formData: formData
                });
            }
            setIsUploadingImageForItem(null);
        } catch (error) {
            console.error('Failed to upload image:', error);
        }
    }, [addItemImage, addPackItemImage, isUploadingImageForItem, includePackItems]);

    // Detail panel component for expanded rows
    const getDetailPage = useCallback((row, rowIndex) => {
        const item = row.original;
        const itemChanges = imageChanges[item.id] || {};
        
        return (
            <ItemImageReviewDetailPage
                item={item}
                itemChanges={itemChanges}
                handleValidateImage={handleValidateImage}
                handleRejectImage={handleRejectImage}
                handleUploadImage={() => setIsUploadingImageForItem(item.id)}
                rowIndex={rowIndex}
                table={table}
                autoCollapsedRows={autoCollapsedRows}
                setAutoCollapsedRows={setAutoCollapsedRows}
            />
        );
    }, [imageChanges, table, autoCollapsedRows, handleValidateImage, handleRejectImage]);

    // Update columnFilters when a form filter changes
    const handleFormFilterChange = (filterList) => {
        setColumnFilters(prev => {
            let updated = [...prev];
            filterList.forEach(({ id, value }) => {
                updated = updated.filter(f => f.id !== id);
                if (value) updated.push({ id, value });
            });
            return updated;
        });
    };

    return (
        <Stack padding={2} spacing={2}>
            <FormControl>
                <FormLabel component="legend">Image Validation Status Filter</FormLabel>
                <ToggleButtonGroup
                    value={validationFilter}
                    exclusive
                    onChange={(event, newValue) => {
                        if (newValue !== null) {
                            updateFilter('validationFilter', newValue);
                        }
                    }}
                    aria-label="validation filter"
                    size="small"
                >
                    <ToggleButton value="unvalidated" aria-label="unvalidated">
                        Has Unvalidated Images
                    </ToggleButton>
                    <ToggleButton value="validated" aria-label="validated">
                        Has Validated Images
                    </ToggleButton>
                    <ToggleButton value="all" aria-label="all">
                        All Items With Images
                    </ToggleButton>
                    <Tooltip 
                        title={!selectedOrganization ? 'Select an organization to filter by items with no images' : ''}
                        arrow
                    >
                        <span>
                            <ToggleButton 
                                value="no-images" 
                                aria-label="no-images"
                                disabled={!selectedOrganization}
                            >
                                Has No Images
                            </ToggleButton>
                        </span>
                    </Tooltip>
                </ToggleButtonGroup>
            </FormControl>

            <FormControl>
                <FormLabel component="legend">Item Type Filter</FormLabel>
                <ToggleButtonGroup
                    value={includePackItems || 'items-only'}
                    exclusive
                    onChange={(event, newValue) => {
                        if (newValue !== null) {
                            updateFilter('includePackItems', newValue);
                        }
                    }}
                    aria-label="item type filter"
                    size="small"
                >
                    <ToggleButton value="pack-items-only" aria-label="pack-items-only">
                        Pack Items
                    </ToggleButton>
                    <ToggleButton value="items-only" aria-label="items-only">
                        Items
                    </ToggleButton>
                </ToggleButtonGroup>
            </FormControl>

            {/* Organization, Hospital, Campaign Filters in a responsive Stack */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
                <FormControl fullWidth margin="dense" sx={{ flex: 1 }}>
                    <FormLabel>Organization</FormLabel>
                    <Select
                        value={selectedOrganization}
                        onChange={e => {
                            const value = e.target.value;
                            const filterUpdates = [
                                { id: 'organization', value },
                                { id: 'hospital', value: '' },
                                { id: 'campaign', value: '' },
                            ];
                            
                            // If unselecting organization and validation filter is "no-images", reset to "unvalidated"
                            if (!value && validationFilter === 'no-images') {
                                filterUpdates.push({ id: 'validationFilter', value: 'unvalidated' });
                            }
                            
                            handleFormFilterChange(filterUpdates);
                        }}
                        disabled={isFetchingOrganizationsFilterValues}
                        fullWidth
                        displayEmpty
                    >
                        <MenuItem value="">All</MenuItem>
                        {organizationList.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth margin="dense" sx={{ flex: 1 }}>
                    <FormLabel>Hospital</FormLabel>
                    <Select
                        value={selectedHospital}
                        onChange={e => {
                            const value = e.target.value;
                            handleFormFilterChange([
                                { id: 'hospital', value },
                                { id: 'campaign', value: '' },
                            ]);
                        }}
                        disabled={isFetchingHospitalsFilterValues || !selectedOrganization}
                        fullWidth
                        displayEmpty
                    >
                        <MenuItem value="">All</MenuItem>
                        {hospitalList.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth margin="dense" sx={{ flex: 1 }}>
                    <FormLabel>Campaign</FormLabel>
                    <Select
                        value={selectedCampaign}
                        onChange={e => {
                            const value = e.target.value;
                            handleFormFilterChange([
                                { id: 'campaign', value },
                            ]);
                        }}
                        disabled={isFetchingCampaignsFilterValues || !selectedHospital || !selectedOrganization}
                        fullWidth
                        displayEmpty
                    >
                        <MenuItem value="">All</MenuItem>
                        {campaignList.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <MaterialReactTable table={table} />
            <UploadImage 
                open={isUploadingImageForItem !== null} 
                setOpen={() => setIsUploadingImageForItem(null)} 
                uploadImage={handleUploadImage}
                isUploading={isUploadingImage || isUploadingPackItemImage}
            />
        </Stack>
    );
}

export default ItemImageReview;
